import { Classroom, Student, Lesson } from "@/types/tutor";
import { useState, useEffect, useRef } from "react";
import api from "@/utils/axios-utils";
import ClassroomInfo from "./ClassroomInfo";
import { buildLessonAnalyticsHref } from "@/features/analytics/service/deepLink";
import { Users, ArrowUpRight } from "lucide-react";

// How each roster provider is named to teachers.
const ROSTER_PROVIDER_NAMES: Record<string, string> = {
  clever: "Clever",
  classlink: "ClassLink",
};

const ClassroomCard = ({
  classroom,
  index,
  onOpen,
}: {
  classroom: Classroom;
  index: number;
  onOpen?: () => void;
}) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchClassroomData = async () => {
    if (hasLoaded) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsLoading(true);
    setError(null);

    try {
      const [studentsResponse, lessonsResponse] = await Promise.all([
        api.get(`/classrooms/${classroom.id}/students`, {
          signal: controller.signal,
        }),
        api.get(`/classrooms/${classroom.id}/lessons`, {
          signal: controller.signal,
        }),
      ]);

      if (controller.signal.aborted) return;
      setStudents(studentsResponse.data);
      setLessons(lessonsResponse.data);
      setHasLoaded(true);
    } catch (err: any) {
      // Don't set error if request was aborted
      if (controller.signal.aborted || err.name === "AbortError") return;

      console.error("Error fetching classroom data:", err);
      setError(err.response?.data?.detail || "Failed to fetch classroom data");
    } finally {
      if (
        abortControllerRef.current === controller &&
        !controller.signal.aborted
      ) {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (!hasLoaded) {
      fetchClassroomData();
    }
  }, [classroom.id, hasLoaded]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const studentCount = hasLoaded
    ? students.length
    : (classroom.student_count ?? 0);

  if (onOpen)
    return (
      <div className="et-group-overview">
        <button type="button" className="et-group-open" onClick={onOpen}>
          <span className="et-group-symbol">
            <Users size={20} />
          </span>
          {/* Phrasing content only: a button cannot hold headings or divs. */}
          <span className="et-group-identity">
            <span className="et-group-name">{classroom.name}</span>
            {classroom.teacher_name && (
              <span className="et-group-teacher">{classroom.teacher_name}</span>
            )}
          </span>
          <span className="et-group-counts">
            <span>
              {studentCount} {studentCount === 1 ? "student" : "students"}
            </span>
            <span>
              {hasLoaded
                ? `${lessons.length} ${lessons.length === 1 ? "lesson" : "lessons"}`
                : error
                  ? "Lessons unavailable"
                  : "Loading lessons…"}
            </span>
          </span>
          <ArrowUpRight className="et-group-arrow" size={18} />
        </button>
        {error && (
          <div role="alert" className="et-group-error">
            <p>{error}</p>
            <button onClick={() => void fetchClassroomData()}>Try again</button>
          </div>
        )}
      </div>
    );

  return (
    <div key={index} className="et-group-card">
      <div className="et-group-detail-meta">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
            <span className="et-count">
              {studentCount} {studentCount === 1 ? "student" : "students"}
            </span>
            {classroom.teacher_name && (
              <span className="et-chip">{classroom.teacher_name}</span>
            )}
            {classroom.synced_from && (
              <span
                className="et-chip et-chip-muted"
                title="Kept in sync with your school's roster. Changes made there update this group."
              >
                Synced from{" "}
                {ROSTER_PROVIDER_NAMES[classroom.synced_from] ??
                  classroom.synced_from}
              </span>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <p role="status" className="et-group-status">
          Loading students and assigned lessons…
        </p>
      ) : error ? (
        <div role="alert" className="et-group-error">
          <p>{error}</p>
          <button type="button" onClick={() => void fetchClassroomData()}>
            Try again
          </button>
        </div>
      ) : (
        <ClassroomInfo
          students={students}
          lessons={lessons}
          classroomId={classroom.id.toString()}
          getAnalyticsHref={(lessonId, lessonName) =>
            buildLessonAnalyticsHref({
              classroomId: classroom.id,
              classroomName: classroom.name,
              lessonId,
              lessonName,
              teacherUserId: classroom.teacher_user_id,
            })
          }
          onLessonUnassign={(lessonId) =>
            setLessons((prev) => prev.filter((l) => l.id !== lessonId))
          }
        />
      )}
    </div>
  );
};

export default ClassroomCard;
